<template>
  <v-container>
    <v-row>
      <!-- Panel izquierdo: Búsqueda e historial -->
      <v-col class="pr-4" cols="4">
        <domain-search-form
          :account-id.sync="selectedAccountId"
          :domain-name.sync="domainName"
          :search-mode.sync="isManualSearchMode"
          @search="performDomainSearch"
          @add-account="handleAddAccount"
          @edit-account="handleEditAccount"
        />

        <domain-history-list
          :history-items="searchHistory"
          @delete="deleteHistoryItem"
          @view="loadHistoryItem"
        />
      </v-col>

      <!-- Panel derecho: Resultados -->
      <v-col v-if="searchResults" cols="8">
        <domain-results-list
          :domains="searchResults"
          :isAccount="selectedAccountId !== null"
          @send-seeds="handleOpenSendSeedsDialog"
        />
      </v-col>
    </v-row>

    <!-- Diálogos -->
    <account-dialog
      :account-data="accountFormData"
      :is-edit-mode="isAccountEditMode"
      :visible.sync="showAccountDialog"
      @cancel="handleCancelAccountDialog"
      @save="handleSaveAccount"
    />

    <send-seeds-dialog
      :seeds="availableSeeds"
      :target-domain="targetDomainForSeeds"
      :visible.sync="showSeedsDialog"
      @cancel="handleCancelSeedsDialog"
      @send="handleSendSeeds"
    />
  </v-container>
</template>

<script>

import service_commservices from "@/services/service_commservices";
import { resourceAction } from "@/services/service_resource_manager";
import DomainSearchForm from "./DomainSearchForm.vue";
import DomainHistoryList from "./DomainHistoryList.vue";
import DomainResultsList from "./DomainResultsList.vue";
import AccountDialog from "./AccountDialog.vue";
import SendSeedsDialog from "./SendSeedsDialog.vue";
import service_user from "@/services/service_user";
import { P_COMMSERVICES_EXCLUSION_LIST_EDIT } from "@/constants/group_permissions";

const HISTORY_STORAGE_KEY = "lastTenRequests";
const MAX_HISTORY_ITEMS = 10;
const SEEDS_FILE_PATH = "//RESOURCES/seeds/Mailjet/seeds.dat";

const DEFAULT_ACCOUNT_DATA = {
  name: "",
  user: "",
  password: "",
  provider: "Mailjet",
  description: "",
  platform: "CertySign"
};

export default {
  name: "DomainsChecker",
  components: {
    DomainSearchForm,
    DomainHistoryList,
    DomainResultsList,
    AccountDialog,
    SendSeedsDialog
  },
  data() {
    return {
      // Búsqueda
      domainName: "",
      selectedAccountId: null,
      isManualSearchMode: false,
      searchResults: null,

      // Historial
      searchHistory: this.loadSearchHistory(),

      // Diálogo de cuenta
      showAccountDialog: false,
      isAccountEditMode: false,
      accountFormData: { ...DEFAULT_ACCOUNT_DATA },

      // Diálogo de semillas
      showSeedsDialog: false,
      targetDomainForSeeds: null,
      availableSeeds: []
    };
  },
  watch: {
    isManualSearchMode() {
      this.resetSearchFields();
    }
  },
  methods: {
    // ============ Búsqueda de dominios ============
    async performDomainSearch() {
      try {
        this.domainName = this.domainName.trim();
        this.searchResults = null;

        const searchParam = this.isManualSearchMode ? this.domainName : null;
        const response = await service_commservices.checkDomain(
          this.selectedAccountId,
          searchParam
        );

        if (response.data.message === "domains_info") {
          this.searchResults = response.data.extra;
          this.saveToHistory(response.data.extra);
        }
      } catch (error) {
        console.error("Error al consultar dominio:", error);
      }
    },

    resetSearchFields() {
      this.selectedAccountId = null;
      this.domainName = "";
    },

    // ============ Gestión del historial ============
    loadSearchHistory() {
      try {
        const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (error) {
        console.error("Error al cargar historial:", error);
        return [];
      }
    },

    saveToHistory(results) {
      const newHistoryItem = {
        date: this.$moment().unix(),
        domain_id: this.selectedAccountId,
        result: results
      };

      const updatedHistory = [newHistoryItem, ...this.searchHistory];

      if (updatedHistory.length > MAX_HISTORY_ITEMS) {
        updatedHistory.pop();
      }

      this.searchHistory = updatedHistory;
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updatedHistory));
    },

    loadHistoryItem(index) {
      const historyItem = this.searchHistory[index];
      this.searchResults = historyItem.result;
      this.selectedAccountId = historyItem.domain_id;
    },

    deleteHistoryItem(index) {
      this.searchHistory.splice(index, 1);
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(this.searchHistory));
    },

    // ============ Gestión de cuentas ============
    handleAddAccount() {
      this.isAccountEditMode = false;
      this.accountFormData = { ...DEFAULT_ACCOUNT_DATA };
      this.showAccountDialog = true;
    },

    handleEditAccount() {
      this.isAccountEditMode = true;
      this.accountFormData = { ...DEFAULT_ACCOUNT_DATA };
      this.showAccountDialog = true;
    },

    async handleSaveAccount(accountData) {
      if (service_user.checkGroupPermission(P_COMMSERVICES_EXCLUSION_LIST_EDIT)) {
        try {
          if (this.isAccountEditMode) {
            await this.updateAccount(accountData);
          } else {
            await this.createAccount(accountData);
          }
          this.showAccountDialog = false;
        } catch (error) {
          console.error("Error al guardar cuenta:", error);
        }

      }
    },

    async createAccount(accountData) {
      if (service_user.checkGroupPermission(P_COMMSERVICES_EXCLUSION_LIST_EDIT)) {
        await service_commservices.addDomain(accountData);
      }
    },

    async updateAccount(accountData) {

      if (service_user.checkGroupPermission(P_COMMSERVICES_EXCLUSION_LIST_EDIT)) {
        const filteredData = this.filterNonEmptyFields(accountData);
        await service_commservices.updateDomain(this.selectedAccountId, filteredData);
      }
    },

    filterNonEmptyFields(data) {
      const filtered = {};
      Object.keys(data).forEach(key => {
        if (data[key] !== "") {
          filtered[key] = data[key];
        }
      });
      return filtered;
    },

    handleCancelAccountDialog() {
      this.showAccountDialog = false;
      this.accountFormData = { ...DEFAULT_ACCOUNT_DATA };
    },

    // ============ Envío de semillas ============
    async handleOpenSendSeedsDialog(domainName) {
      try {
        this.targetDomainForSeeds = domainName;
        const seeds = await this.fetchAvailableSeeds();
        this.availableSeeds = seeds;
        this.showSeedsDialog = true;
      } catch (error) {
        console.error("Error al cargar semillas:", error);
      }
    },

    async fetchAvailableSeeds() {
      const response = await resourceAction("download_file", SEEDS_FILE_PATH);
      const decodedContent = atob(response.data.filecontent);

      return decodedContent
        .split("\n")
        .map(seed => seed.trim())
        .filter(seed => seed.length > 0);
    },

    async handleSendSeeds(selectedSeeds) {

      if (service_user.checkGroupPermission(P_COMMSERVICES_EXCLUSION_LIST_EDIT)) {
        try {
          const confirmMessage = `¿Estás seguro que deseas enviar ${selectedSeeds.length} semilla(s) al dominio '${this.targetDomainForSeeds}'?`;

          if (confirm(confirmMessage)) {
            await service_commservices.sendSeeds(
              this.selectedAccountId,
              selectedSeeds,
              this.targetDomainForSeeds
            );
            this.showSeedsDialog = false;
          }
        } catch (error) {
          console.error("Error al enviar semillas:", error);
        }
      }
    },

    handleCancelSeedsDialog() {
      this.showSeedsDialog = false;
      this.targetDomainForSeeds = null;
      this.availableSeeds = [];
    }
  }
};
</script>

<style lang="scss" scoped>
// Los estilos específicos se mantienen en los componentes individuales
</style>
